<template>
  <div class="payment-management">
    <div class="payment-management__header">
      <router-link class="payment-management__header-back" to="/mypage"
        >＜ {{ $t("paymentManagement.backToMyPage") }}</router-link
      >
    </div>
    <div class="payment-management__content">
      <div class="payment-management__content-title">{{ $t("paymentManagement.title") }}</div>
      <div class="payment-management__content-box">
        <!-- 表示優先順位: 決済エラー(hasPaymentError) > 解約済み(isCanceled) > 通常(WILL_CHANGEを含む) -->
        <div class="payment-management__content-item-header">
          <div class="payment-management__content-item-title">{{ $t("paymentManagement.account.title") }}</div>
          <!-- プレミアム会員が解約済みの場合、解約済みバッジを表示(Web/App共通) -->
          <div v-if="!isFree && !hasPaymentError && isCanceled" class="payment-management__canceled-badge">
            {{ $t("paymentManagement.account.canceled") }}
          </div>
          <!-- プレミアム会員(Web)の場合、次回更新日を表示（subscription未取得時はラベルのみの空表示を避けるため非表示） -->
          <div
            v-else-if="!isFree && !isAppMember && !hasPaymentError && !isCanceled && planCurrentPeriodEnd"
            class="payment-management__content-item-title"
          >
            {{ $t("paymentManagement.account.nextRenewalDate") }}
          </div>
        </div>
        <div class="payment-management__content-item-body">
          <div class="payment-management__plan-info">
            <!-- 無料会員 -->
            <div v-if="isFree" class="payment-management__plan-free">
              {{ $t("paymentManagement.account.freeMember") }}
            </div>
            <!-- プレミアム会員 -->
            <div v-else class="payment-management__plan-premium-wrapper">
              <div class="payment-management__plan-premium">
                <img
                  src="@/assets/img/icon/premium.png"
                  :alt="$t('paymentManagement.account.premiumAlt')"
                  class="payment-management__premium-icon"
                />
                <span>{{ $t("paymentManagement.account.premium") }}（{{ billingPlatformName }}）</span>
              </div>
              <!-- アプリ課金かつ解約済みの場合は終了日を案内、アプリ課金(解約前)は次回更新日/退会の案内 -->
              <div v-if="isAppMember && !hasPaymentError && isCanceled" class="payment-management__plan-note">
                {{ $t("paymentManagement.account.appCanceledNote") }}
              </div>
              <div v-else-if="isAppMember" class="payment-management__plan-note">
                {{ $t("paymentManagement.account.appNote") }}
              </div>
              <!-- Web課金かつ解約済みの場合、終了日を案内（終了日が取得できない場合は日付なしの文言） -->
              <div v-else-if="!hasPaymentError && isCanceled" class="payment-management__plan-note">
                {{
                  planCanceledEndDate
                    ? $t("paymentManagement.account.canceledNote", { date: planCanceledEndDate })
                    : $t("paymentManagement.account.canceledNoteFallback")
                }}
              </div>
              <!-- Web課金かつ解約前(決済エラー中を含む)の場合、契約確認ボタンを表示 -->
              <button
                v-else
                type="button"
                class="payment-management__text-link-button"
                @click="onClickModifyPaymentInfo"
              >
                {{ $t("paymentManagement.account.confirmSubscription") }}
              </button>
            </div>
          </div>
          <!-- 無料会員の場合、アップグレードボタンを表示 -->
          <template v-if="isFree && !hasPaymentError">
            <button class="payment-management__button upgrade" @click="onClickUpgrade">
              <div class="payment-management__premium-icon-wrapper">
                <img
                  src="@/assets/img/icon/premium.png"
                  :alt="$t('paymentManagement.account.premiumAlt')"
                  class="payment-management__premium-icon"
                />
              </div>
              <span>{{ $t("paymentManagement.account.upgrade") }}</span>
            </button>
          </template>
          <!-- プレミアム会員(Web)かつ解約前(決済エラーなし)の場合、次回更新日と支払い金額を表示 -->
          <template v-else-if="!isAppMember && !hasPaymentError && !isCanceled">
            <div class="payment-management__plan-schedule">
              <div>{{ planCurrentPeriodEnd }}</div>
              <div class="payment-management__plan-price">
                {{ planPrice }}{{ $t("paymentManagement.account.currency") }}
              </div>
            </div>
          </template>
        </div>
      </div>
      <!-- アプリ課金かつ解約済みの場合、再継続の案内を表示 -->
      <div v-if="isAppMember && !hasPaymentError && isCanceled" class="payment-management__app-recontract-note">
        <IconInfo class="payment-management__app-recontract-note-icon" />
        <span>{{ $t("paymentManagement.account.appRecontractNote") }}</span>
      </div>
      <div class="payment-management__content-box">
        <div class="payment-management__content-item-header">
          <div class="payment-management__content-item-title">{{ $t("paymentManagement.paymentMethod.title") }}</div>
        </div>
        <div class="payment-management__content-item-body">
          <div class="payment-management__payment-method">
            <!-- 未登録の場合 -->
            <template v-if="!billingInfo.cardInfo">
              <div>{{ $t("paymentManagement.paymentMethod.notRegistered") }}</div>
            </template>
            <!-- 登録済みの場合 -->
            <template v-else>
              <IconCreditCard class="payment-management__card-number-icon" />
              <!-- カード番号 -->
              <div class="payment-management__payment-method-card-number">**** {{ cardLast4 }}</div>
              <!-- 有効期限 -->
              <div class="payment-management__payment-method-card-expiration-date">
                {{ cardExpYear }} / {{ cardExpMonth }}
              </div>
            </template>
            <!-- 変更ボタン: App課金でも都度課金でStripeアカウントが紐づく場合があるため、platformに関わらず表示する -->
            <button
              v-if="billingInfo.stripeId"
              type="button"
              class="payment-management__text-link-button"
              @click="onClickModifyPaymentInfo"
            >
              {{ $t("paymentManagement.paymentMethod.modify") }}
            </button>
          </div>
        </div>
      </div>
      <payment-description />
      <div v-if="!isFree && !isAppMember" class="payment-management__bottom-links">
        <!-- 解約済みの場合、契約確認ボタンをここに表示（解約前はプレミアム表示の下に表示済み） -->
        <button
          v-if="!hasPaymentError && isCanceled"
          type="button"
          class="payment-management__text-link-button"
          @click="onClickModifyPaymentInfo"
        >
          {{ $t("paymentManagement.account.confirmSubscription") }}
        </button>
        <router-link v-else class="payment-management__unsubscription" :to="{ name: 'unsubscriptionAnnounce' }">{{
          $t("paymentManagement.unsubscribe")
        }}</router-link>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { format } from "date-fns";
import { mapGetters } from "vuex";
import { BillingClient } from "@/lib/clients";
import {
  BillingInfo,
  CreatePortalSessionInput,
  PlanPricing,
  SubscriptionRenewalState,
  isAppPlatform,
  resolveIsCanceled,
} from "@/lib/models/billing";
import PaymentDescription from "@/components/organisms/subscription/PaymentDescription.vue";
import { getCanceledEndDateFormat, getDateFnsLocale } from "@/lib/utils/i18n/locale";
import IconCreditCard from "~icons/figma/CreditCard";
import IconInfo from "~icons/figma/Info";

const billingClient = new BillingClient();

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },

  components: {
    IconCreditCard,
    IconInfo,
    PaymentDescription,
  },

  async created() {
    window.scrollTo(0, 0);

    await Promise.all([
      this.$store.dispatch("billingModule/fetchBillingInfo"),
      // pricing取得に失敗してもページ表示は継続する
      this.$store.dispatch("billingModule/fetchPricing").catch(() => {}),
    ]);
  },

  computed: {
    ...mapGetters("billingModule", ["hasPaymentError"]),
    billingInfo() {
      return this.$store.getters["billingModule/billingInfo"] as BillingInfo;
    },
    pricing() {
      return this.$store.getters["billingModule/pricing"] as PlanPricing | null;
    },
    isFree() {
      const { billingInfo } = this;
      return billingInfo?.plan === 0;
    },
    isAppMember() {
      const { billingInfo } = this;
      return isAppPlatform(billingInfo?.platform);
    },
    billingPlatformName() {
      const { isAppMember } = this;
      if (isAppMember) {
        return this.$t("paymentManagement.account.platform.app");
      }
      return this.$t("paymentManagement.account.platform.web");
    },
    cardLast4() {
      return this.billingInfo?.cardInfo?.last4;
    },
    cardExpYear() {
      return this.billingInfo?.cardInfo?.exp_year;
    },
    cardExpMonth() {
      return this.billingInfo?.cardInfo?.exp_month;
    },
    planPrice() {
      const { billingInfo, pricing } = this;
      if (billingInfo && pricing) {
        switch (billingInfo.plan) {
          default:
          case 0:
            return "0";
          case 1:
            return pricing.monthlyPrice.toLocaleString("ja-JP");
          case 2:
            return pricing.yearlyPrice.toLocaleString("ja-JP");
        }
      }
      // 有料ユーザーで pricing 未取得の場合は "-" を表示
      if (billingInfo && billingInfo.plan !== 0) {
        return "-";
      }
      return "0";
    },
    planCurrentPeriodEnd() {
      const { billingInfo } = this;
      const { subscription } = billingInfo;
      if (subscription && subscription.currentPeriodEnd) {
        return format(subscription.currentPeriodEnd, "YYYY/MM/DD");
      }
      return undefined;
    },
    renewalState() {
      const { billingInfo } = this;
      return billingInfo?.subscription?.renewalState ?? null;
    },
    // NOTE: App の契約状態モデルを正とする。WILL_CHANGE（プラン変更予定）は契約継続中のため
    // ACTIVE と同じ表示（=isCanceled: false）にする。Web課金はSubscription Scheduleを使っていないため、
    // 実際にWILL_CHANGEが返るのはApp課金ユーザーがWebを見た場合のみで、その場合はappNoteが表示される。
    isCanceled() {
      const { renewalState, billingInfo, isFree } = this;
      return resolveIsCanceled(renewalState, { isFree, isAutoRenewal: billingInfo?.subscription?.isAutoRenewal });
    },
    planCanceledEndDate() {
      const { billingInfo } = this;
      const { subscription } = billingInfo;
      if (subscription && subscription.currentPeriodEnd) {
        const locale = getDateFnsLocale(this.$i18n.locale);
        const formatStr = getCanceledEndDateFormat(this.$i18n.locale);
        return format(subscription.currentPeriodEnd, formatStr, { locale });
      }
      return undefined;
    },
  },

  methods: {
    async onClickModifyPaymentInfo() {
      const payload: CreatePortalSessionInput = {
        returnUrl: `${window.location.origin}/payment-management`,
      };
      const session = await billingClient.createPortalSession(payload);

      window.location.href = session.url;
    },
    onClickUpgrade() {
      this.$router.push({ name: "subscriptionAnnounce" });
    },
  },
});

interface Props {}

interface Data {}

interface Computed {
  billingInfo: BillingInfo;
  pricing: PlanPricing | null;
  isFree: boolean;
  isAppMember: boolean;
  billingPlatformName: string;
  cardLast4: string;
  cardExpYear: string;
  cardExpMonth: string;
  planPrice?: string;
  planCurrentPeriodEnd?: string;
  renewalState: SubscriptionRenewalState | null;
  isCanceled: boolean;
  hasPaymentError: boolean;
  planCanceledEndDate?: string;
}

interface Methods {
  onClickModifyPaymentInfo(): Promise<void>;
  onClickUpgrade(): void;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.payment-management {
  width: 750px;
  height: 100%;
  margin: 0 auto;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);

  @include mq(sp) {
    width: 100%;
  }
}

.payment-management__header {
  margin: 24px 0;

  @include mq(sp) {
    padding: 0 16px;
  }

  &-back {
    font-size: var(--font-size-xs);
    color: var(--color-primary);
    text-decoration: none;
  }
}

.payment-management__content {
  @include mq(sp) {
    padding: 0 16px;
    box-sizing: border-box;
  }
}

.payment-management__content-title {
  margin-bottom: 10px;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.payment-management__content-box {
  margin-bottom: 10px;
  background-color: var(--color-surface-base);
  border-radius: var(--radius-sm);
  padding: 20px;

  @include mq(sp) {
    padding: 16px;
  }
}

.payment-management__app-recontract-note {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 10px;
  font-size: var(--font-size-xs);
  color: var(--color-text-low);
}

.payment-management__app-recontract-note-icon {
  flex-shrink: 0;
  font-size: var(--font-size-sm);
}

.payment-management__content-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.payment-management__content-item-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.payment-management__content-item-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  margin-bottom: 10px;
  color: var(--color-text-normal);
}

.payment-management__button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  line-height: 1.5;
  color: var(--color-text-normal);
  background: var(--color-surface-muted);
  border: none;
  border-radius: var(--radius-lg);
  padding: 4px 8px;
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: 1px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }

  &.upgrade {
    color: var(--color-text-on-fixed-bg);
    background: var(--color-brand-premium);
    padding: 4px 10px 4px 6px;
  }
}

.payment-management__premium-icon-wrapper {
  width: 24px;
  height: 24px;
  padding: 4px;
  box-sizing: border-box;
  flex-shrink: 0;
  background: var(--color-background-normal);
  background-clip: padding-box;
  border-radius: var(--radius-circle);
  display: flex;
  align-items: center;
  justify-content: center;
}

.payment-management__premium-icon {
  width: 16px;
  height: 16px;
}

.payment-management__plan-premium-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.payment-management__plan-premium {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-brand-premium);
}

.payment-management__text-link-button {
  position: relative;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  @include hover-overlay;
}

.payment-management__plan-note {
  font-size: var(--font-size-xs);
  color: var(--color-text-low);
}

.payment-management__canceled-badge {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-invert);
  background: var(--color-background-invert);
  border-radius: var(--radius-rounded);
  padding: 4px 10px;
}

.payment-management__plan-schedule {
  text-align: right;
}

.payment-management__plan-price {
  color: var(--color-text-low);
  margin-top: 4px;
}

.payment-management__plan-free {
  font-size: var(--font-size-sm);
}

.payment-management__payment-method {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  font-size: var(--font-size-sm);
}

.payment-management__payment-method-card-number {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
}

.payment-management__card-number-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-md);
}

.payment-management__payment-method-card-expiration-date {
  color: var(--color-text-low);
}

.payment-management__bottom-links {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.payment-management__unsubscription {
  display: flex;
  align-items: center;
  color: var(--color-warning);
  text-decoration: none;
}
</style>
